import { apiClient } from './api';
import { usersService } from './users.service';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

// ============ Enums & Constants ============

export const MATTER_STATUSES = [
  'INTAKE',
  'UNDER_REVIEW',
  'ACTIVE',
  'ON_HOLD',
  'SETTLEMENT_NEGOTIATION',
  'SETTLED',
  'CLOSED',
  'ARCHIVED',
] as const;

export const MATTER_TYPES = [
  'LITIGATION_PLAINTIFF',
  'LITIGATION_DEFENDANT',
  'REGULATORY_INVESTIGATION',
  'CONTRACT_DISPUTE',
  'EMPLOYMENT_MATTER',
  'INTELLECTUAL_PROPERTY',
  'CORPORATE_TRANSACTION',
  'ADVISORY_COUNSELING',
  'INSURANCE_CLAIM',
  'GOVERNMENT_INQUIRY',
  'INTERNAL_INVESTIGATION',
  'OTHER',
] as const;

export const MATTER_PRIORITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;

export const CONFIDENTIALITY_LEVELS = [
  'STANDARD',
  'SENSITIVE',
  'HIGHLY_SENSITIVE',
  'ATTORNEY_CLIENT_PRIVILEGED',
] as const;

export const EVENT_TYPES = [
  'FILING',
  'HEARING',
  'DEPOSITION',
  'DISCOVERY',
  'SETTLEMENT_OFFER',
  'DECISION_ORDER',
  'STATUS_CHANGE',
  'INTERNAL_MEETING',
  'EXTERNAL_COMMUNICATION',
  'DEADLINE',
  'NOTE',
] as const;

export const COST_CATEGORIES = [
  'INTERNAL_COUNSEL_TIME',
  'EXTERNAL_COUNSEL_FEES',
  'COURT_COSTS',
  'EXPERT_WITNESS_FEES',
  'SETTLEMENT_AMOUNT',
  'JUDGMENT_AMOUNT',
  'MEDIATION_ARBITRATION_FEES',
  'TRAVEL_COSTS',
  'OTHER_EXPENSES',
] as const;

export const HOLD_STATUSES = ['DRAFT', 'ACTIVE', 'RELEASED'] as const;

// ============ Type Labels ============

export const MATTER_TYPE_LABELS: Record<string, string> = {
  LITIGATION_PLAINTIFF: 'Litigation (Plaintiff)',
  LITIGATION_DEFENDANT: 'Litigation (Defendant)',
  REGULATORY_INVESTIGATION: 'Regulatory Investigation',
  CONTRACT_DISPUTE: 'Contract Dispute',
  EMPLOYMENT_MATTER: 'Employment Matter',
  INTELLECTUAL_PROPERTY: 'Intellectual Property',
  CORPORATE_TRANSACTION: 'Corporate Transaction',
  ADVISORY_COUNSELING: 'Advisory / Counseling',
  INSURANCE_CLAIM: 'Insurance Claim',
  GOVERNMENT_INQUIRY: 'Government Inquiry',
  INTERNAL_INVESTIGATION: 'Internal Investigation',
  OTHER: 'Other',
};

export const MATTER_STATUS_LABELS: Record<string, string> = {
  INTAKE: 'Intake',
  UNDER_REVIEW: 'Under Review',
  ACTIVE: 'Active',
  ON_HOLD: 'On Hold',
  SETTLEMENT_NEGOTIATION: 'Settlement Negotiation',
  SETTLED: 'Settled',
  CLOSED: 'Closed',
  ARCHIVED: 'Archived',
};

export const CONFIDENTIALITY_LABELS: Record<string, string> = {
  STANDARD: 'Standard',
  SENSITIVE: 'Sensitive',
  HIGHLY_SENSITIVE: 'Highly Sensitive',
  ATTORNEY_CLIENT_PRIVILEGED: 'Attorney-Client Privileged',
};

export const COST_CATEGORY_LABELS: Record<string, string> = {
  INTERNAL_COUNSEL_TIME: 'Internal Counsel Time',
  EXTERNAL_COUNSEL_FEES: 'External Counsel Fees',
  COURT_COSTS: 'Court Costs',
  EXPERT_WITNESS_FEES: 'Expert Witness Fees',
  SETTLEMENT_AMOUNT: 'Settlement Amount',
  JUDGMENT_AMOUNT: 'Judgment Amount',
  MEDIATION_ARBITRATION_FEES: 'Mediation / Arbitration Fees',
  TRAVEL_COSTS: 'Travel Costs',
  OTHER_EXPENSES: 'Other Expenses',
};

export const EVENT_TYPE_LABELS: Record<string, string> = {
  FILING: 'Filing',
  HEARING: 'Hearing',
  DEPOSITION: 'Deposition',
  DISCOVERY: 'Discovery',
  SETTLEMENT_OFFER: 'Settlement Offer',
  DECISION_ORDER: 'Decision / Order',
  STATUS_CHANGE: 'Status Change',
  INTERNAL_MEETING: 'Internal Meeting',
  EXTERNAL_COMMUNICATION: 'External Communication',
  DEADLINE: 'Deadline',
  NOTE: 'Note',
};

export const TRACKING_CATEGORY_LABELS: Record<string, string> = {
  PRIVACY_ATRI: 'Privacy / ATRI',
  LEGAL: 'Legal',
  BANKRUPTCY: 'Bankruptcy',
  MISCELLANEOUS: 'Miscellaneous',
};

export const TRACKING_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export const HIPAA_STATUS_LABELS: Record<string, string> = {
  REPORTED: 'Reported',
  UNDER_INVESTIGATION: 'Under Investigation',
  RISK_ASSESSMENT: 'Risk Assessment',
  NOTIFICATION_IN_PROGRESS: 'Notification in Progress',
  CLOSED: 'Closed',
};

export const HIPAA_SOURCE_LABELS: Record<string, string> = {
  CUSTOMER_SERVICE: 'Customer Service',
  INTERNAL_DISCOVERY: 'Internal Discovery',
  BUSINESS_ASSOCIATE: 'Business Associate',
  MEMBER_COMPLAINT: 'Member Complaint',
  AUDIT_FINDING: 'Audit Finding',
  OTHER: 'Other',
};

// VIS-204: "Type of PHI Disclosed" grouped catalog. Single source of truth
// for the create form, the detail display, and the detail-edit form so the
// three surfaces can never drift. Grouped into Member Information and
// Provider Information per the requirements doc.
export interface PhiTypeOption {
  value: string;
  label: string;
}
export interface PhiTypeGroup {
  group: string;
  options: PhiTypeOption[];
}

export const PHI_TYPE_GROUPS: PhiTypeGroup[] = [
  {
    group: 'Member Information',
    options: [
      { value: 'SUBSCRIBER_FIRST_NAME', label: 'Subscriber First Name' },
      { value: 'SUBSCRIBER_LAST_NAME', label: 'Subscriber Last Name' },
      { value: 'SUBSCRIBER_INITIAL', label: 'Subscriber Initial' },
      { value: 'DEPENDENT_FIRST_NAME', label: 'Dependent First Name' },
      { value: 'DEPENDENT_LAST_NAME', label: 'Dependent Last Name' },
      { value: 'DEPENDENT_INITIAL', label: 'Dependent Initial' },
      { value: 'FULL_ADDRESS', label: 'Full Address' },
      { value: 'PARTIAL_ADDRESS', label: 'Partial Address' },
      { value: 'DATE_OF_BIRTH', label: 'Date of Birth' },
      { value: 'PARTIAL_DATE_OF_BIRTH', label: 'Partial Date of Birth' },
      { value: 'SSN_LAST_FOUR', label: 'SSN (last four digits only)' },
      { value: 'PARTIAL_SSN', label: 'Partial SSN' },
      { value: 'SUBSCRIBER_ID_NUMBER', label: 'Subscriber ID Number' },
      { value: 'PARTIAL_ID_NUMBER', label: 'Partial ID Number' },
      { value: 'PROCEDURE_DESCRIPTION', label: 'Procedure Description' },
      { value: 'PROCEDURE_CODE', label: 'Procedure Code' },
      { value: 'AMOUNTS_BILLED', label: 'Amounts Billed' },
      { value: 'PAYMENT_AMOUNT', label: 'Payment Amount' },
      { value: 'CHECK_PAYMENT_NUMBER', label: 'Check/Payment Number' },
      { value: 'CLAIM_NUMBER', label: 'Claim Number' },
      { value: 'DATE_OF_SERVICE', label: 'Date of Service' },
      { value: 'GROUP_NAME', label: 'Group Name' },
      { value: 'GROUP_NUMBER', label: 'Group Number' },
      { value: 'PAYMENT_DATE', label: 'Payment Date' },
      { value: 'EFFECTIVE_DATES', label: 'Effective Dates' },
      { value: 'PLAN_STATUS', label: 'Plan Status' },
      { value: 'EMAIL_ADDRESS', label: 'Email Address' },
      { value: 'PHONE_NUMBER', label: 'Phone Number' },
      { value: 'BILLING_PERIOD', label: 'Billing Period' },
      { value: 'GENDER', label: 'Gender' },
      { value: 'OTHER', label: 'Other' },
    ],
  },
  {
    group: 'Provider Information',
    options: [
      { value: 'PROVIDER_NAME', label: 'Provider Name' },
      { value: 'PROVIDER_LICENSE_NUMBER', label: 'Provider License Number' },
      { value: 'PROVIDER_NPI_NUMBER', label: 'Provider NPI Number' },
    ],
  },
];

// Display label lookup. Includes the legacy pre-VIS-204 values so historical
// incidents that stored them still render a human-readable label.
export const PHI_TYPE_LABELS: Record<string, string> = {
  NAME: 'Name',
  ADDRESS: 'Address',
  SSN: 'SSN',
  MEDICAL_RECORD_NUMBER: 'Medical Record #',
  HEALTH_PLAN_ID: 'Health Plan ID',
  DIAGNOSIS_TREATMENT_INFO: 'Diagnosis / Treatment',
  FINANCIAL_INFO: 'Financial Info',
  FINANCIAL_INFORMATION: 'Financial Info',
  ...Object.fromEntries(
    PHI_TYPE_GROUPS.flatMap((g) => g.options.map((o) => [o.value, o.label]))
  ),
};

export const RISK_DETERMINATION_LABELS: Record<string, string> = {
  BREACH_CONFIRMED: 'Breach Confirmed',
  LOW_PROBABILITY: 'Low Probability of Compromise',
  EXCEPTION_APPLIES: 'Exception Applies',
  PENDING: 'Pending',
};

export const BUSINESS_ENTITY_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  ARCHIVED: 'Archived',
};

export type BusinessEntityStatus = keyof typeof BUSINESS_ENTITY_STATUS_LABELS;

export interface BusinessEntity {
  id: string;
  name: string;
  description?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  address?: string | null;
  status: BusinessEntityStatus;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface BusinessEntityInput {
  name: string;
  description?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  address?: string | null;
  status?: BusinessEntityStatus;
}

export const CUSTOM_CONTRACT_TYPE_LABELS: Record<string, string> = {
  CUSTOM_GROUP_AGREEMENT: 'Custom Group Agreement',
  CUSTOM_RIDER: 'Custom Rider',
  CUSTOM_AMENDMENT: 'Custom Amendment',
  CUSTOM_BENEFIT_PLAN: 'Custom Benefit Plan',
  OTHER: 'Other',
};

export const CUSTOM_CONTRACT_PRIORITY_LABELS: Record<string, string> = {
  STANDARD: 'Standard',
  EXPEDITED: 'Expedited',
  URGENT: 'Urgent',
};

export const CUSTOM_CONTRACT_STATUS_LABELS: Record<string, string> = {
  REQUESTED: 'Requested',
  DRAFTING: 'Drafting',
  INTERNAL_REVIEW: 'Internal Review',
  CLIENT_REVIEW: 'Client Review',
  REVISIONS: 'Revisions',
  FINAL: 'Final',
  EXECUTED: 'Executed',
};

// ============ Interfaces ============

/**
 * Pagination meta as emitted by `createPaginationMeta` in
 * libs/api/src/responses.ts. Lives on `meta.pagination` in the envelope.
 */
export interface HipaaListPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/**
 * Canonical HIPAA incident shape as emitted by the hipaa-service
 * controllers (apps/hipaa-service/src/controllers/hipaa-incidents.controller.ts).
 * Mirrors the `HipaaIncidentWithGroups` Prisma shape — fields are
 * optional/nullable to match the schema and tolerate older records that
 * may not have every column populated.
 *
 * Page-level views (HipaaIncidentsList, HipaaIncidentDetails) declare
 * narrower local interfaces that are structurally compatible with this
 * type. Centralizing the contract here means a future server-side rename
 * (e.g. `items` → `rows`, or removing a field) becomes a compile error
 * at the service boundary instead of an empty page at runtime.
 */
/**
 * HIPAA Group — employer group covered by a BAA (apps/hipaa-service Prisma `Group`).
 * Re-used across HIPAA incidents; the per-group `breachNotificationTimeframeDays`
 * is the "custom days to notify" referenced in VIS-131.
 */
export interface HipaaGroup {
  id: string;
  groupName: string;
  groupCode: string;
  fundingType: 'FULLY_INSURED' | 'SELF_FUNDED_ASO' | 'OTHER';
  breachNotificationTimeframeDays: number;
  baResearchInitialResponseDays?: number;
  baResearchFinalResponseDays?: number;
  retentionYears?: number;
  annualHhsFilingResponsibility?: 'DDMN_FILES_ON_BEHALF' | 'GROUP_FILES' | 'NOT_APPLICABLE';
  baaEffectiveDate?: string | null;
  baaExpirationDate?: string | null;
  primaryContactName?: string | null;
  primaryContactEmail?: string | null;
  primaryContactPhone?: string | null;
  active: boolean;
  deletedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateHipaaGroupInput {
  groupName: string;
  groupCode: string;
  fundingType: 'FULLY_INSURED' | 'SELF_FUNDED_ASO' | 'OTHER';
  breachNotificationTimeframeDays?: number;
}

/**
 * Shape of an affected-group entry returned alongside a HIPAA incident.
 * Mirrors the Prisma Group model (apps/hipaa-service/prisma/schema.prisma).
 * Legacy `groupType` / `customReportingDays` aliases preserved for any
 * older incidents whose payloads still echo the pre-VIS-131 shape.
 */
export interface HipaaIncidentAffectedGroup {
  id?: string;
  groupName: string;
  groupCode?: string;
  fundingType?: 'FULLY_INSURED' | 'SELF_FUNDED_ASO' | 'OTHER';
  breachNotificationTimeframeDays?: number;
  // Legacy fields — older clients/payloads used these names.
  groupType?: string;
  customReportingDays?: number | null;
}

export interface HipaaIncidentStepItem {
  id: string;
  itemText: string;
  isChecked: boolean;
  checkedBy?: string | null;
  checkedAt?: string | null;
  notes?: string | null;
}

// ── HIPAA incident document types ────────────────────────────────────────────

export interface HipaaIncidentDoc {
  id: string;
  incidentId: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  storagePath: string;
  storageContainer: string;
  sourceType: 'MANUAL' | 'SUB_STEP_ARTIFACT';
  sourceStepKey: string | null;
  sourceSubStepKey: string | null;
  uploadedById: string | null;
  uploadedByName: string | null;
  version: number;
  displayName: string | null;
  description: string | null;
  changeNotes: string | null;
  parentDocumentId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// VIS-208 — notification LETTER templates surfaced on the Notifications tab.
export type NotificationLetterTemplateType =
  | 'DDMN_GROUP'
  | 'DDMN_MEMBER'
  | 'DDNE_GROUP'
  | 'DDNE_MEMBER'
  | 'HVN_GROUP'
  | 'HVN_MEMBER';

export interface NotificationLetterTemplate {
  id: string;
  templateType: NotificationLetterTemplateType;
  version: number;
  isCurrent: boolean;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  changeNotes: string | null;
  uploadedById: string | null;
  uploadedByName: string | null;
  createdAt: string;
}

// Display metadata for the six templates. Order matches the Notifications-tab
// layout; the label is what compliance staff recognize the letter by.
export const NOTIFICATION_LETTER_TEMPLATES: Array<{
  type: NotificationLetterTemplateType;
  label: string;
}> = [
  { type: 'DDMN_GROUP', label: 'DDMN Group Notification Letter' },
  { type: 'DDMN_MEMBER', label: 'DDMN Member Notification Letter' },
  { type: 'DDNE_GROUP', label: 'DDNE Group Notification' },
  { type: 'DDNE_MEMBER', label: 'DDNE Member Notification' },
  { type: 'HVN_GROUP', label: 'HVN DeltaVision Group Notification' },
  { type: 'HVN_MEMBER', label: 'HVN DeltaVision Member Notification' },
];

export interface HipaaActivity {
  id: string;
  incidentId: string;
  activityType: string;
  title: string;
  details: string | null;
  performedById: string | null;
  performedByName: string | null;
  createdAt: string;
}

// ── VIS-126 sub-step types ───────────────────────────────────────────────────

export type HipaaSubStepRequirement =
  | 'ATTESTATION'
  | 'ARTIFACT_OR_NA'
  | 'NOTE_OR_NA';

export type HipaaSubStepStatus =
  | 'NOT_STARTED'
  | 'COMPLETE'
  | 'NOT_APPLICABLE';

export type HipaaNotificationOutcome =
  | 'NO_NOTIFICATION'
  | 'SCOPED_NOTIFICATION'
  | 'BROADER_NOTIFICATION';

export interface HipaaIncidentSubStep {
  id: string;
  stepId: string;
  subStepKey: string;
  ordinal: number;
  requirement: HipaaSubStepRequirement;
  status: HipaaSubStepStatus;
  completedByUserId?: string | null;
  completedAt?: string | null;
  noteText?: string | null;
  attachmentPath?: string | null;
  naReasonText?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface HipaaIncidentSubStepDefinition {
  subStepKey: string;
  ordinal: number;
  title: string;
  description?: string;
  requirement: HipaaSubStepRequirement;
}

export interface HipaaIncidentSubStepsPayload {
  items: HipaaIncidentSubStep[];
  definitions: HipaaIncidentSubStepDefinition[];
  notificationOutcome: HipaaNotificationOutcome | null;
}

export interface HipaaIncidentLifecycleStep {
  id: string;
  stepNumber: number;
  stepName: string;
  stepKey: string;
  status: string;
  notificationOutcome?: HipaaNotificationOutcome | null;
  items?: HipaaIncidentStepItem[];
}

export interface HipaaIncident {
  id: string;
  incidentNumber: string;
  status: string;
  privacyReferenceNumber?: string | null;
  source?: string | null;
  description?: string | null;
  riskDetermination?: string | null;
  reportingDate?: string | null;
  dateOfDiscovery?: string | null;
  dateOfIncident?: string | null;
  notificationDueDate?: string | null;
  notificationDueDateOverridden?: boolean | null;
  individualsAffectedEstimate?: number | null;
  individualsAffectedConfirmed?: number | null;
  assignedHandler?: string | null;
  assignedHandlerId?: string | null;
  phiTypes?: string[];
  phiTypesInvolved?: string[];
  // VIS-204: free-text detail captured when the "Other" PHI type is checked.
  phiOtherDetails?: string | null;
  affectedGroups?: HipaaIncidentAffectedGroup[];
  // VIS-129: optional Business Entity attribution.
  businessEntityId?: string | null;
  businessEntity?: BusinessEntity | null;
  lifecycleSteps?: HipaaIncidentLifecycleStep[];
  closureReason?: string | null;
  closureNote?: string | null;
  closedAt?: string | null;
  // VIS-205: Breach Risk Assessment Tool worksheet fields.
  raMemberName?: string | null;
  raGroupName?: string | null;
  raGroupNumber?: string | null;
  raMemberIdNumber?: string | null;
  raFactorNaturePhi?: string | null;
  raFactorUnauthorizedPerson?: string | null;
  raFactorAcquiredViewed?: string | null;
  raFactorMitigation?: string | null;
  raFactorOther?: string | null;
  raLowProbabilityConclusion?: boolean | null;
  raConclusionExplanation?: string | null;
  raCompletedBy?: string | null;
  raCompletedByTitle?: string | null;
  raCompletedDate?: string | null;
  raApprovedBy?: string | null;
  // VIS-205: controls Notifications-tab visibility.
  notificationsEnabled?: boolean | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string | null;
  deletedAt?: string | null;
}

// ── HIPAA Incident Dashboard (VIS-71, spec 3.10.8) ──────────────────────────

export interface HipaaDashboardFilters {
  groupId?: string;
  source?: string;
  severity?: string;
  fundingType?: 'FULLY_INSURED' | 'SELF_FUNDED_ASO' | 'OTHER';
  volumeGranularity?: 'MONTH' | 'QUARTER' | 'YEAR';
  reportingYear?: number;
}

export interface HipaaDashboardThresholds {
  amberDays: number;
  yellowDays: number;
}

export interface HipaaOpenIncidentsBreakdown {
  total: number;
  byStatus: Array<{ status: string; count: number }>;
  byHandler: Array<{ handlerId: string | null; count: number }>;
  bySource: Array<{ source: string; count: number }>;
}

export type HipaaDeadlineTier = 'RED' | 'AMBER' | 'YELLOW' | 'GREEN' | 'UNSET';

export interface HipaaNotificationDeadlineRow {
  id: string;
  incidentNumber: string;
  status: string;
  notificationDueDate: string | null;
  individualsAffectedEstimate: number | null;
  daysUntil: number | null;
  tier: HipaaDeadlineTier;
}

export interface HipaaNotificationDeadlines {
  items: HipaaNotificationDeadlineRow[];
  tierCounts: Record<HipaaDeadlineTier, number>;
  thresholds: HipaaDashboardThresholds;
}

export interface HipaaLifecycleStepSnapshot {
  stepNumber: number;
  stepKey: string;
  status: string;
}

export interface HipaaMajorBreachSnapshot {
  category: 'MEDIA' | 'HHS_500_PLUS' | 'STATE_REGULATOR';
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'NOT_APPLICABLE';
}

export interface HipaaLifecycleProgressRow {
  id: string;
  incidentNumber: string;
  status: string;
  stepsCompleted: number;
  totalSteps: number;
  steps: HipaaLifecycleStepSnapshot[];
  majorBreach: HipaaMajorBreachSnapshot[];
}

export interface HipaaVolumeBucket {
  bucket: string;
  count: number;
}

export interface HipaaVolumeMetrics {
  granularity: 'MONTH' | 'QUARTER' | 'YEAR';
  byBucket: HipaaVolumeBucket[];
  bySource: Array<{ source: string; count: number }>;
  byGroup: Array<{ groupId: string; groupName: string; count: number }>;
  byFundingType: Array<{ fundingType: string; count: number }>;
  total: number;
}

export interface HipaaAnnualHhsByGroup {
  groupId: string;
  groupName: string;
  fundingType: string;
  filingResponsibility: string;
  incidentCount: number;
  filedCount: number;
  unfiledCount: number;
}

export interface HipaaAnnualHhsReportingStatus {
  reportingYear: number;
  deadline: string;
  daysUntilDeadline: number;
  byGroup: HipaaAnnualHhsByGroup[];
  totalIncidents: number;
  totalUnfiled: number;
}

export interface HipaaCompletionRatePoint {
  bucket: string;
  rate: number;
  closed: number;
  fullyComplete: number;
}

export interface HipaaCompletionRate {
  current: number;
  closedCount: number;
  fullyCompleteCount: number;
  trend: HipaaCompletionRatePoint[];
}

export interface HipaaMyAssignedIncidentRow {
  id: string;
  incidentNumber: string;
  status: string;
  riskDetermination: string;
  notificationDueDate: string | null;
  reportingDate: string;
  description: string;
}

export interface HipaaMyAssignedIncidents {
  items: HipaaMyAssignedIncidentRow[];
  total: number;
}

export interface HipaaDashboardPayload {
  filters: HipaaDashboardFilters & {
    volumeGranularity: 'MONTH' | 'QUARTER' | 'YEAR';
    reportingYear: number;
  };
  thresholds: HipaaDashboardThresholds;
  widgets: {
    openIncidents: HipaaOpenIncidentsBreakdown;
    notificationDeadlines: HipaaNotificationDeadlines;
    lifecycleProgress: HipaaLifecycleProgressRow[];
    volumeMetrics: HipaaVolumeMetrics;
    annualHhsStatus: HipaaAnnualHhsReportingStatus;
    completionRate: HipaaCompletionRate;
    myAssigned: HipaaMyAssignedIncidents;
  };
  generatedAt: string;
}

// ── Major Breach Manual-Tracking (VIS-69, spec 3.10.7) ──────────────────────

export type MajorBreachCategory = 'MEDIA' | 'HHS_500_PLUS' | 'STATE_REGULATOR';

export type MajorBreachNotificationStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'NOT_APPLICABLE';

export interface MajorBreachAttachment {
  id: string;
  notificationId: string;
  filename: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  filePath: string;
  uploadedBy?: string | null;
  uploadedAt: string;
}

export interface MajorBreachNotification {
  id: string;
  incidentId: string;
  category: MajorBreachCategory;
  status: MajorBreachNotificationStatus;
  dateSent?: string | null;
  dateFiled?: string | null;
  jurisdictions: string[];
  notes?: string | null;
  responsiblePersonUserId?: string | null;
  ocrSubmissionId?: string | null;
  statutoryDeadlinePerState?: Record<string, string> | null;
  attachments: MajorBreachAttachment[];
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MajorBreachNotificationInput {
  status?: MajorBreachNotificationStatus;
  dateSent?: string | null;
  dateFiled?: string | null;
  jurisdictions?: string[];
  notes?: string | null;
  responsiblePersonUserId?: string | null;
  ocrSubmissionId?: string | null;
  statutoryDeadlinePerState?: Record<string, string> | null;
}

// ── Notification Letters (VIS-206) ──────────────────────────────────────────
// Fillable brand breach-notification letters. Keyed by the same
// `NotificationLetterTemplateType` as the VIS-208 template-file manager and the
// `HipaaSentNotification` audit trail, so the three cannot drift apart.

export type NotificationLetterVariant = 'MEMBER' | 'GROUP';
export type LetterFieldType = 'text' | 'textarea' | 'date' | 'select';

export interface NotificationLetterField {
  token: string;
  label: string;
  type: LetterFieldType;
  prefillFrom?: string;
  options?: string[];
  required?: boolean;
}

export interface HipaaNotificationLetter {
  key: NotificationLetterTemplateType;
  brand: string;
  brandLabel: string;
  variant: NotificationLetterVariant;
  displayName: string;
  fields: NotificationLetterField[];
  /** Token → pre-filled value derived from the incident. */
  prefill: Record<string, string>;
}

export interface HipaaNotificationLetterPreview {
  letterKey: NotificationLetterTemplateType;
  displayName: string;
  /** Merged letter body as constrained rich text (`<p>`/`<strong>`). */
  body: string;
  missingTokens: string[];
}

export interface HipaaLetterRenderOutputs {
  docxBase64: string;
  pdfBase64: string;
  docxBytes: number;
  pdfBytes: number;
}

export interface HipaaSentNotification {
  id: string;
  incidentId: string;
  letterKey: NotificationLetterTemplateType;
  brand: string;
  variant: NotificationLetterVariant;
  recipientName?: string | null;
  // PHI — only present on the send response, not on the list endpoint
  // (the Notifications tab shows metadata only; the list query omits these).
  filledFields?: Record<string, string>;
  renderedBody?: string;
  sentByUserId?: string | null;
  sentAt: string;
  createdAt: string;
}

export interface HipaaSendNotificationLetterResult {
  sentNotification: HipaaSentNotification;
  outputs: HipaaLetterRenderOutputs;
}

/**
 * Input payload for HIPAA create / update. The strict Zod schema lives
 * in libs/validation (`createHipaaIncidentSchema`, `updateHipaaIncidentSchema`)
 * and validates server-side. Keeping the client-side input loose (a plain
 * record) avoids forcing form refactors as part of this typing pass —
 * the existing form payload uses some legacy field names (`phiTypes`,
 * `assignedHandler`) that don't match the Zod schema (`phiTypesInvolved`,
 * `assignedHandlerId`). That's a real, separate bug worth its own
 * follow-up; do not paper over it here.
 */
export type HipaaIncidentInput = Record<string, unknown>;

export interface LegalMatter {
  id: string;
  matterNumber: string;
  title: string;
  type: string;
  subType?: string;
  status: string;
  priority: string;
  confidentiality: string;
  dateOpened: string;
  dateOfIncident?: string;
  statuteOfLimitations?: string;
  nextKeyDate?: string;
  dateClosed?: string;
  expectedResolution?: string;
  leadAttorneyId: string;
  leadAttorneyName: string;
  supportingCounsel: string[];
  assignedParalegals: string[];
  businessStakeholders: string[];
  externalCounsel: string[];
  opposingParty?: Record<string, unknown> | null;
  courtInfo?: Record<string, unknown> | null;
  relatedVendorIds: string[];
  relatedIncidentIds: string[];
  relatedContractIds: string[];
  relatedMatterIds: string[];
  summary?: string;
  legalStrategyNotes?: string;
  internalNotes?: string;
  tags: string[];
  budgetAmount?: number | string | null;
  budgetCurrency: string;
  reserveAmount?: number | string | null;
  outcomeType?: string;
  outcomeSummary?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
  events?: MatterEvent[];
  costs?: MatterCost[];
  documents?: MatterDocument[];
  holds?: LegalHold[];
  statusHistory?: StatusHistoryEntry[];
  _count?: {
    events: number;
    costs: number;
    documents: number;
    holds: number;
  };
}

export interface StatusHistoryEntry {
  id: string;
  fromStatus?: string;
  toStatus: string;
  reason?: string;
  createdAt: string;
  createdBy?: string;
}

export interface MatterEvent {
  id: string;
  matterId: string;
  eventType: string;
  title: string;
  description?: string;
  eventDate: string;
  participants?: string[];
  createdBy?: string;
  createdAt: string;
}

export interface MatterCost {
  id: string;
  matterId: string;
  category: string;
  description: string;
  amount: number | string;
  currency: string;
  costDate: string;
  vendorName?: string;
  invoiceNumber?: string;
  createdAt: string;
}

export interface MatterDocument {
  id: string;
  matterId: string;
  fileName: string;
  fileSize?: number;
  mimeType?: string;
  category?: string;
  privilege?: string;
  description?: string;
  folder?: string;
  uploadedBy?: string;
  createdAt: string;
  deletedAt?: string;
}

export interface LegalHold {
  id: string;
  holdName: string;
  matterId: string;
  matterTitle?: string;
  status: string;
  scopeDescription?: string;
  dataSources: string[];
  holdNoticeText?: string;
  issuedDate?: string;
  releasedDate?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  custodians?: HoldCustodian[];
  auditLogs?: HoldAuditEntry[];
  _count?: { custodians: number };
}

export interface HoldCustodian {
  id: string;
  holdId: string;
  userId?: string;
  custodianName: string;
  custodianEmail: string;
  department?: string;
  ackStatus: string;
  ackDate?: string;
  remindersSent: number;
  lastReminderAt?: string;
  createdAt: string;
}

export interface HoldAuditEntry {
  id: string;
  holdId: string;
  action: string;
  details?: string;
  performedBy?: string;
  createdAt: string;
}

export interface FinancialSummary {
  budgetAmount: number | null;
  totalCosts: number;
  reserveAmount: number | null;
  budgetUtilization: number | null;
  costByCategory: Record<string, number>;
}

export interface FinancialDashboard {
  totalSpend: number;
  byType: Array<{ type: string; total: number; count?: number }>;
  topMatters: Array<{ id: string; title: string; matterNumber: string; total: number; totalCost?: number }>;
  budgetVariance: Array<{
    id: string;
    title: string;
    matterNumber: string;
    budget: number | null;
    budgetAmount?: number | null;
    actual: number;
    actualCost?: number;
    variance: number | null;
  }>;
  totalReserves: number;
}

// ============ Service ============

const BASE = '/api/v1/legal';

/**
 * Build a user-facing message from a failed API response. On a Zod
 * validation failure the envelope's top-level message is generic (e.g.
 * "Invalid HIPAA incident payload") while the actionable detail lives in
 * `error.details.issues`. Surface the first issue (with its field path) so
 * the user sees *which* field is wrong instead of the opaque message.
 */
function validationMessage(
  error: { message?: string; details?: unknown } | undefined,
  fallback: string
): string {
  const issues = (error?.details as { issues?: Array<{ path?: unknown[]; message?: string }> })
    ?.issues;
  const first = issues?.[0];
  if (first?.message) {
    const field = Array.isArray(first.path) && first.path.length ? `${first.path.join('.')}: ` : '';
    return `${field}${first.message}`;
  }
  return error?.message ?? fallback;
}

/**
 * Matter endpoints return the matter either bare in `data` (current
 * legal-service shape) or wrapped as `data.matter` (legacy shape) — accept both.
 */
function unwrapMatter(data: LegalMatter | { matter: LegalMatter }): LegalMatter {
  return 'matter' in data ? data.matter : data;
}

export const legalService = {
  // ── Matters ──────────────────────────────────────────────────────────────

  async getMatters(params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    priority?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.page) {query.set('page', String(params.page));}
    if (params?.limit) {query.set('limit', String(params.limit));}
    if (params?.status) {query.set('status', params.status);}
    if (params?.type) {query.set('type', params.type);}
    if (params?.priority) {query.set('priority', params.priority);}
    if (params?.search) {query.set('search', params.search);}
    if (params?.sortBy) {query.set('sortBy', params.sortBy);}
    if (params?.sortOrder) {query.set('sortOrder', params.sortOrder);}

    const res = await apiClient.get<{
      matters: LegalMatter[];
      pagination: { total: number; page: number; limit: number; totalPages: number };
    }>(`${BASE}/matters?${query.toString()}`);

    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch matters');
    }
    return res.data;
  },

  async getMatter(id: string) {
    const res = await apiClient.get<LegalMatter | { matter: LegalMatter }>(`${BASE}/matters/${id}`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch matter');
    }
    return unwrapMatter(res.data);
  },

  async createMatter(payload: Record<string, unknown>) {
    const res = await apiClient.post<LegalMatter | { matter: LegalMatter }>(`${BASE}/matters`, payload);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to create matter');
    }
    return unwrapMatter(res.data);
  },

  async updateMatter(id: string, payload: Record<string, unknown>) {
    const res = await apiClient.put<LegalMatter | { matter: LegalMatter }>(`${BASE}/matters/${id}`, payload);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to update matter');
    }
    return unwrapMatter(res.data);
  },

  async updateMatterStatus(id: string, status: string, reason?: string) {
    const res = await apiClient.patch<LegalMatter | { matter: LegalMatter }>(`${BASE}/matters/${id}/status`, {
      status,
      reason,
    });
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to update status');
    }
    return unwrapMatter(res.data);
  },

  // ── Events ────────────────────────────────────────────────────────────────

  async getEvents(matterId: string) {
    const res = await apiClient.get<{ events: MatterEvent[] }>(
      `${BASE}/matters/${matterId}/events`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch events');
    }
    return res.data.events;
  },

  async createEvent(matterId: string, payload: Record<string, unknown>) {
    const res = await apiClient.post<{ event: MatterEvent }>(
      `${BASE}/matters/${matterId}/events`,
      payload
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to create event');
    }
    return res.data.event;
  },

  // ── Costs ─────────────────────────────────────────────────────────────────

  async getCosts(matterId: string) {
    const res = await apiClient.get<{ costs: MatterCost[] }>(
      `${BASE}/matters/${matterId}/costs`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch costs');
    }
    return res.data.costs;
  },

  async createCost(matterId: string, payload: Record<string, unknown>) {
    const res = await apiClient.post<{ cost: MatterCost }>(
      `${BASE}/matters/${matterId}/costs`,
      payload
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to create cost');
    }
    return res.data.cost;
  },

  async deleteCost(matterId: string, costId: string) {
    const res = await apiClient.delete(`${BASE}/matters/${matterId}/costs/${costId}`);
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Failed to delete cost');
    }
  },

  async getFinancialSummary(matterId: string) {
    const res = await apiClient.get<{ summary: FinancialSummary }>(
      `${BASE}/matters/${matterId}/financial-summary`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch financial summary');
    }
    return res.data.summary;
  },

  async getFinancialDashboard() {
    const res = await apiClient.get<Record<string, unknown>>(
      `${BASE}/matters/reporting/financial-dashboard`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch financial dashboard');
    }
    // Map API field names to frontend interface
    const raw = res.data;
    const byTypeRaw = (raw.byType ?? raw.spendByMatterType ?? []) as Array<Record<string, unknown>>;
    const topRaw = (raw.topMatters ?? raw.top10BySpend ?? []) as Array<Record<string, unknown>>;
    const varianceRaw = (raw.budgetVariance ?? []) as Array<Record<string, unknown>>;
    return {
      totalSpend: (raw.totalSpend ?? 0) as number,
      byType: byTypeRaw.map((r) => ({ type: r.type as string, total: (r.total ?? 0) as number, count: (r.count ?? 0) as number })),
      topMatters: topRaw.map((r) => ({ id: r.id as string, title: r.title as string, matterNumber: r.matterNumber as string, total: ((r.total ?? r.totalCost ?? 0) as number) })),
      budgetVariance: varianceRaw.map((r) => ({ id: r.id as string, title: r.title as string, matterNumber: r.matterNumber as string, budget: (r.budget ?? r.budgetAmount ?? null) as number | null, actual: ((r.actual ?? r.actualCost ?? 0) as number), variance: (r.variance ?? null) as number | null })),
      totalReserves: (raw.totalReserves ?? 0) as number,
    } as FinancialDashboard;
  },

  // ── Documents ─────────────────────────────────────────────────────────────

  async getDocuments(matterId: string) {
    const res = await apiClient.get<{ documents: MatterDocument[] }>(
      `${BASE}/matters/${matterId}/documents`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch documents');
    }
    return res.data.documents;
  },

  async uploadDocument(matterId: string, file: File, meta: Record<string, unknown>) {
    const res = await apiClient.uploadFile<{ document: MatterDocument }>(
      `${BASE}/matters/${matterId}/documents`,
      file,
      undefined,
      meta
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to upload document');
    }
    return res.data.document;
  },

  async deleteDocument(matterId: string, docId: string) {
    const res = await apiClient.delete(`${BASE}/matters/${matterId}/documents/${docId}`);
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Failed to delete document');
    }
  },

  // ── Holds ─────────────────────────────────────────────────────────────────

  async getHolds(params?: { page?: number; limit?: number; status?: string; matterId?: string }) {
    const query = new URLSearchParams();
    if (params?.page) {query.set('page', String(params.page));}
    if (params?.limit) {query.set('limit', String(params.limit));}
    if (params?.status) {query.set('status', params.status);}
    if (params?.matterId) {query.set('matterId', params.matterId);}

    const res = await apiClient.get<{
      holds: LegalHold[];
      pagination: { total: number; page: number; limit: number; totalPages: number };
    }>(`${BASE}/holds?${query.toString()}`);

    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch holds');
    }
    return res.data;
  },

  async getHold(id: string) {
    const res = await apiClient.get<{ hold: LegalHold }>(`${BASE}/holds/${id}`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch hold');
    }
    return res.data.hold;
  },

  async createHold(payload: Record<string, unknown>) {
    const res = await apiClient.post<{ hold: LegalHold }>(`${BASE}/holds`, payload);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to create hold');
    }
    return res.data.hold;
  },

  async updateHold(id: string, payload: Record<string, unknown>) {
    const res = await apiClient.put<{ hold: LegalHold }>(`${BASE}/holds/${id}`, payload);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to update hold');
    }
    return res.data.hold;
  },

  async activateHold(id: string) {
    const res = await apiClient.patch<{ hold: LegalHold }>(`${BASE}/holds/${id}/activate`, {});
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to activate hold');
    }
    return res.data.hold;
  },

  async releaseHold(id: string) {
    const res = await apiClient.patch<{ hold: LegalHold }>(`${BASE}/holds/${id}/release`, {});
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to release hold');
    }
    return res.data.hold;
  },

  async addCustodian(holdId: string, payload: Record<string, unknown>) {
    const res = await apiClient.post<{ custodian: HoldCustodian }>(
      `${BASE}/holds/${holdId}/custodians`,
      payload
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to add custodian');
    }
    return res.data.custodian;
  },

  async updateCustodian(holdId: string, custodianId: string, payload: Record<string, unknown>) {
    const res = await apiClient.put<{ custodian: HoldCustodian }>(
      `${BASE}/holds/${holdId}/custodians/${custodianId}`,
      payload
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to update custodian');
    }
    return res.data.custodian;
  },

  async removeCustodian(holdId: string, custodianId: string) {
    const res = await apiClient.delete(
      `${BASE}/holds/${holdId}/custodians/${custodianId}`
    );
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Failed to remove custodian');
    }
  },

  async acknowledgeCustodian(holdId: string, custodianId: string) {
    const res = await apiClient.patch<{ custodian: HoldCustodian }>(
      `${BASE}/holds/${holdId}/custodians/${custodianId}/acknowledge`,
      {}
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to acknowledge hold');
    }
    return res.data.custodian;
  },

  async getHoldAuditLog(holdId: string) {
    const res = await apiClient.get<{ auditLog: HoldAuditEntry[] }>(
      `${BASE}/holds/${holdId}/audit-log`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch audit log');
    }
    const data = res.data as { auditLog?: HoldAuditEntry[] } | HoldAuditEntry[];
    return Array.isArray(data) ? data : (data.auditLog ?? []);
  },

  // ── Helpers ───────────────────────────────────────────────────────────────

  getStatusConfig(status: string): { label: string; color: 'success' | 'warning' | 'danger' | 'secondary' | 'primary' } {
    const map: Record<string, { label: string; color: 'success' | 'warning' | 'danger' | 'secondary' | 'primary' }> = {
      INTAKE: { label: 'Intake', color: 'secondary' },
      UNDER_REVIEW: { label: 'Under Review', color: 'warning' },
      ACTIVE: { label: 'Active', color: 'success' },
      ON_HOLD: { label: 'On Hold', color: 'warning' },
      SETTLEMENT_NEGOTIATION: { label: 'Settlement Negotiation', color: 'primary' },
      SETTLED: { label: 'Settled', color: 'success' },
      CLOSED: { label: 'Closed', color: 'secondary' },
      ARCHIVED: { label: 'Archived', color: 'secondary' },
    };
    return map[status] ?? { label: status, color: 'secondary' };
  },

  getPriorityConfig(priority: string): { label: string; color: 'success' | 'warning' | 'danger' | 'secondary' } {
    const map: Record<string, { label: string; color: 'success' | 'warning' | 'danger' | 'secondary' }> = {
      CRITICAL: { label: 'Critical', color: 'danger' },
      HIGH: { label: 'High', color: 'warning' },
      MEDIUM: { label: 'Medium', color: 'secondary' },
      LOW: { label: 'Low', color: 'success' },
    };
    return map[priority] ?? { label: priority, color: 'secondary' };
  },

  getHoldStatusConfig(status: string): { label: string; color: 'success' | 'warning' | 'danger' | 'secondary' } {
    const map: Record<string, { label: string; color: 'success' | 'warning' | 'danger' | 'secondary' }> = {
      DRAFT: { label: 'Draft', color: 'secondary' },
      ACTIVE: { label: 'Active', color: 'success' },
      RELEASED: { label: 'Released', color: 'warning' },
    };
    return map[status] ?? { label: status, color: 'secondary' };
  },

  getValidTransitions(status: string): string[] {
    const transitions: Record<string, string[]> = {
      INTAKE: ['UNDER_REVIEW'],
      UNDER_REVIEW: ['ACTIVE', 'CLOSED'],
      ACTIVE: ['ON_HOLD', 'SETTLEMENT_NEGOTIATION', 'CLOSED'],
      ON_HOLD: ['ACTIVE'],
      SETTLEMENT_NEGOTIATION: ['SETTLED', 'ACTIVE'],
      SETTLED: ['CLOSED'],
      CLOSED: ['ARCHIVED'],
      ARCHIVED: [],
    };
    return transitions[status] ?? [];
  },

  formatCurrency(amount: number | string | null | undefined, currency = 'USD'): string {
    if (amount === null || amount === undefined) {return '—';}
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) {return '—';}
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num);
  },

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) {return '—';}
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },

  formatFileSize(bytes: number | undefined): string {
    if (!bytes) {return '—';}
    if (bytes < 1024) {return `${bytes} B`;}
    if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  },

  // ── Reference Lists ───────────────────────────────────────────────────────

  async getReferenceListItems(slug: string) {
    const res = await apiClient.get<{ list: unknown; items: unknown[] }>(`${BASE}/reference-lists/${slug}/items`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch reference list items');
    }
    return (res.data as { items: unknown[] }).items ?? res.data;
  },

  // ── Tracking Requests ─────────────────────────────────────────────────────

  async getTrackingRequests(params?: {
    page?: number;
    limit?: number;
    category?: string;
    status?: string;
    assignedTo?: string;
    search?: string;
    overdue?: boolean;
  }) {
    const query = new URLSearchParams();
    if (params?.page) {query.set('page', String(params.page));}
    if (params?.limit) {query.set('limit', String(params.limit));}
    if (params?.category) {query.set('category', params.category);}
    if (params?.status) {query.set('status', params.status);}
    if (params?.assignedTo) {query.set('assignedTo', params.assignedTo);}
    if (params?.search) {query.set('search', params.search);}
    if (params?.overdue !== undefined) {query.set('overdue', String(params.overdue));}

    const res = await apiClient.get<Record<string, unknown>>(`${BASE}/tracking?${query.toString()}`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch tracking requests');
    }
    return res.data;
  },

  async getTrackingRequest(id: string) {
    const res = await apiClient.get<{ request: unknown }>(`${BASE}/tracking/${id}`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch tracking request');
    }
    return (res.data).request ?? res.data;
  },

  async createTrackingRequest(data: unknown) {
    const res = await apiClient.post<Record<string, unknown>>(`${BASE}/tracking`, data);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to create tracking request');
    }
    return res.data;
  },

  async updateTrackingRequest(id: string, data: unknown) {
    const res = await apiClient.put<Record<string, unknown>>(`${BASE}/tracking/${id}`, data);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to update tracking request');
    }
    return res.data;
  },

  async deleteTrackingRequest(id: string) {
    const res = await apiClient.delete(`${BASE}/tracking/${id}`);
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Failed to delete tracking request');
    }
  },

  async getTrackingDashboard() {
    const res = await apiClient.get<Record<string, unknown>>(`${BASE}/tracking/dashboard`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch tracking dashboard');
    }
    return res.data;
  },

  // ── HIPAA Groups (employer groups covered by a BAA, VIS-131) ─────────────

  /**
   * GET /api/v1/legal/hipaa/groups — list selectable Groups for
   * attribution on a HIPAA incident form. Defaults to active, non-
   * soft-deleted Groups; pass `active: false` or `includeAll: true`
   * to widen.
   */
  async listHipaaGroups(params?: {
    active?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<HipaaGroup[]> {
    const query = new URLSearchParams();
    if (params?.active !== undefined) {
      query.set('active', String(params.active));
    } else {
      query.set('active', 'true');
    }
    if (params?.search) {query.set('search', params.search);}
    query.set('page', String(params?.page ?? 1));
    query.set('limit', String(params?.limit ?? 100));

    const res = await apiClient.get<{ items: HipaaGroup[] }>(
      `${BASE}/hipaa/groups?${query.toString()}`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch HIPAA groups');
    }
    return res.data.items ?? [];
  },

  /**
   * POST /api/v1/legal/hipaa/groups — create a new Group inline from
   * the HIPAA incident create/edit form (VIS-131 / FR-GRP-1).
   */
  async createHipaaGroup(input: CreateHipaaGroupInput): Promise<HipaaGroup> {
    const res = await apiClient.post<HipaaGroup>(`${BASE}/hipaa/groups`, input);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to create HIPAA group');
    }
    return res.data;
  },

  /**
   * PATCH /api/v1/legal/hipaa/groups/:id — edit an existing Group's
   * core fields inline from the HIPAA incident form (VIS-131 / FR-GRP-3).
   * The edit applies globally to the Group, affecting every incident that
   * reuses it; per-incident overrides live on the affected-group row, not
   * here. Write access is role-gated (LEGAL_ADMIN / ADMIN / SUPER_ADMIN)
   * at the hipaa-service; the gateway proxies PATCH through unchanged.
   */
  async updateHipaaGroup(
    id: string,
    input: Partial<CreateHipaaGroupInput>
  ): Promise<HipaaGroup> {
    const res = await apiClient.patch<HipaaGroup>(`${BASE}/hipaa/groups/${id}`, input);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to update HIPAA group');
    }
    return res.data;
  },

  // ── HIPAA Incidents ───────────────────────────────────────────────────────

  async getHipaaIncidents(params?: {
    page?: number;
    limit?: number;
    status?: string;
    handler?: string;
    search?: string;
  }): Promise<{ items: unknown[]; pagination: HipaaListPagination | undefined }> {
    const query = new URLSearchParams();
    if (params?.page) {query.set('page', String(params.page));}
    if (params?.limit) {query.set('limit', String(params.limit));}
    if (params?.status) {query.set('status', params.status);}
    if (params?.handler) {query.set('handler', params.handler);}
    if (params?.search) {query.set('search', params.search);}

    // Type the response shape directly on the generic so `items` is
    // already `HipaaIncident[]` — no downstream cast needed (eslint's
    // `no-unnecessary-type-assertion` rule flags the cast otherwise).
    const res = await apiClient.get<{ items: HipaaIncident[] }>(`${BASE}/hipaa?${query.toString()}`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch HIPAA incidents');
    }
    // hipaa-service emits pagination at `meta.pagination` via
    // ApiResponseBuilder.success(..., createPaginationMeta(...)) — see
    // libs/api/src/responses.ts. The web-client's shared ApiResponse type
    // doesn't model that nested shape, so narrow it locally rather than
    // widening the global type and risking unrelated breakage.
    const meta = res.meta as { pagination?: HipaaListPagination } | undefined;
    // Normalize each list item's API field names to match the interface,
    // mirroring the single-incident normalization in getHipaaIncident.
    const items = (res.data.items ?? []).map((incident) => {
      const raw = incident as unknown as Record<string, unknown>;
      if (!incident.notificationDueDate) {
        incident.notificationDueDate =
          (raw.breachNotificationDueDateOverride ?? raw.breachNotificationDueDate) as string | null ?? null;
      }
      if (
        (incident.individualsAffectedEstimate === undefined || incident.individualsAffectedEstimate === null) &&
        raw.estimatedIndividualsAffected !== undefined &&
        raw.estimatedIndividualsAffected !== null
      ) {
        incident.individualsAffectedEstimate = raw.estimatedIndividualsAffected as number;
      }
      if (!incident.assignedHandler && raw.assignedHandlerId) {
        incident.assignedHandlerId = raw.assignedHandlerId as string;
      }
      return incident;
    });
    return {
      items,
      pagination: meta?.pagination,
    };
  },

  /**
   * GET /api/v1/legal/hipaa/:id — returns the incident directly in
   * `data` (no `{ incident }` wrapper). Some JSON-blob columns
   * (`phiTypes`, `affectedGroups`) are stored as strings by older
   * records; coerce them to arrays so render code can iterate safely.
   */
  async getHipaaIncident(id: string): Promise<HipaaIncident> {
    type StepRecord = {
      id: string;
      stepNumber: number;
      stepKey: string;
      status: string;
      notificationOutcome?: HipaaNotificationOutcome | null;
    };
    type StepDefinition = { stepKey: string; title: string };
    type StepsPayload = { items: StepRecord[]; definitions: StepDefinition[] };

    const [res, stepsRes] = await Promise.all([
      apiClient.get<HipaaIncident>(`${BASE}/hipaa/${id}`),
      apiClient.get<StepsPayload>(`${BASE}/hipaa/${id}/steps`).catch(() => null),
    ]);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch HIPAA incident');
    }
    const incident = res.data;
    const raw = incident as unknown as Record<string, unknown>;

    // Normalize API field names → interface field names. The backend emits
    // Prisma camelCase column names which differ from the legacy frontend names.
    if (!incident.phiTypes) {
      incident.phiTypes = (raw.phiTypesInvolved as string[] | undefined) ?? [];
    } else if (typeof incident.phiTypes === 'string') {
      try { incident.phiTypes = JSON.parse(incident.phiTypes) as string[]; }
      catch { incident.phiTypes = []; }
    }
    if (
      (incident.individualsAffectedEstimate === undefined || incident.individualsAffectedEstimate === null) &&
      raw.estimatedIndividualsAffected !== undefined &&
      raw.estimatedIndividualsAffected !== null
    ) {
      incident.individualsAffectedEstimate = raw.estimatedIndividualsAffected as number;
    }
    if (
      (incident.individualsAffectedConfirmed === undefined || incident.individualsAffectedConfirmed === null) &&
      raw.confirmedIndividualsAffected !== undefined &&
      raw.confirmedIndividualsAffected !== null
    ) {
      incident.individualsAffectedConfirmed = raw.confirmedIndividualsAffected as number;
    }
    if (!incident.notificationDueDate) {
      incident.notificationDueDate = (raw.breachNotificationDueDateOverride ?? raw.breachNotificationDueDate) as string | null ?? null;
    }
    // Resolve handler display name from user service when the API returns
    // only the ID (which is always the case — handler name is never embedded).
    if (!incident.assignedHandler && incident.assignedHandlerId) {
      try {
        const user = await usersService.getUserById(incident.assignedHandlerId);
        const fullName = user.fullName ?? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        incident.assignedHandler = fullName !== '' ? fullName : null;
      } catch {
        // non-critical — leave as null
      }
    }

    if (incident.affectedGroups && typeof incident.affectedGroups === 'string') {
      try {
        incident.affectedGroups = JSON.parse(incident.affectedGroups) as HipaaIncidentAffectedGroup[];
      } catch {
        incident.affectedGroups = [];
      }
    }
    if (stepsRes?.success && stepsRes.data) {
      const defsByKey = new Map(stepsRes.data.definitions.map((d) => [d.stepKey, d]));
      incident.lifecycleSteps = stepsRes.data.items.map((s) => ({
        id: s.id,
        stepNumber: s.stepNumber,
        stepKey: s.stepKey,
        stepName: defsByKey.get(s.stepKey)?.title ?? s.stepKey,
        status: s.status,
        completedAt: (s as unknown as { completedAt?: string | null }).completedAt ?? null,
        notificationOutcome: s.notificationOutcome ?? null,
        items: [],
      }));
    }
    return incident;
  },

  async createHipaaIncident(data: HipaaIncidentInput): Promise<HipaaIncident> {
    const res = await apiClient.post<HipaaIncident>(`${BASE}/hipaa`, data);
    if (!res.success || !res.data) {
      throw new Error(validationMessage(res.error, 'Failed to create HIPAA incident'));
    }
    return res.data;
  },

  async updateHipaaIncident(
    id: string,
    data: HipaaIncidentInput
  ): Promise<HipaaIncident> {
    const res = await apiClient.patch<HipaaIncident>(`${BASE}/hipaa/${id}`, data);
    if (!res.success || !res.data) {
      throw new Error(validationMessage(res.error, 'Failed to update HIPAA incident'));
    }
    return res.data;
  },

  async updateHipaaIncidentStatus(
    id: string,
    data: { status: string; reason?: string }
  ): Promise<HipaaIncident> {
    const res = await apiClient.patch<HipaaIncident>(`${BASE}/hipaa/${id}/status`, data);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to update HIPAA incident status');
    }
    return res.data;
  },

  // ── VIS-126 lifecycle step + sub-step endpoints ────────────────────────

  async completeHipaaLifecycleStep(
    incidentId: string,
    stepKey: string,
    data: {
      status?: 'IN_PROGRESS' | 'COMPLETE' | 'NOT_APPLICABLE';
      attachmentPath?: string;
      attachmentContainer?: string;
      naReasonText?: string;
    }
  ): Promise<Record<string, unknown>> {
    const res = await apiClient.post<Record<string, unknown>>(
      `${BASE}/hipaa/${incidentId}/steps/${stepKey}/complete`,
      data
    );
    if (!res.success || !res.data) {
      const err = new Error(res.error?.message ?? 'Failed to update HIPAA step') as Error & {
        code?: string;
        details?: unknown;
      };
      err.code = res.error?.code;
      err.details = (res.error as { details?: unknown } | undefined)?.details;
      throw err;
    }
    return res.data;
  },

  async listHipaaLifecycleSubSteps(
    incidentId: string,
    stepKey: string
  ): Promise<HipaaIncidentSubStepsPayload> {
    const res = await apiClient.get<HipaaIncidentSubStepsPayload>(
      `${BASE}/hipaa/${incidentId}/steps/${stepKey}/sub-steps`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch sub-steps');
    }
    return res.data;
  },

  async completeHipaaLifecycleSubStep(
    incidentId: string,
    stepKey: string,
    subStepKey: string,
    data: {
      noteText?: string;
      attachmentPath?: string;
      attachmentContainer?: string;
    }
  ): Promise<HipaaIncidentSubStep> {
    const res = await apiClient.post<HipaaIncidentSubStep>(
      `${BASE}/hipaa/${incidentId}/steps/${stepKey}/sub-steps/${subStepKey}/complete`,
      data
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to complete sub-step');
    }
    return res.data;
  },

  async uploadHipaaSubStepArtifact(
    incidentId: string,
    stepKey: string,
    subStepKey: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<HipaaIncidentSubStep> {
    const res = await apiClient.uploadFile<HipaaIncidentSubStep>(
      `${BASE}/hipaa/${incidentId}/steps/${stepKey}/sub-steps/${subStepKey}/upload`,
      file,
      onProgress
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to upload artifact');
    }
    return res.data;
  },

  async markHipaaLifecycleSubStepNotApplicable(
    incidentId: string,
    stepKey: string,
    subStepKey: string,
    data: { naReasonText: string }
  ): Promise<HipaaIncidentSubStep> {
    const res = await apiClient.post<HipaaIncidentSubStep>(
      `${BASE}/hipaa/${incidentId}/steps/${stepKey}/sub-steps/${subStepKey}/not-applicable`,
      data
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to mark sub-step N/A');
    }
    return res.data;
  },

  async reopenHipaaLifecycleSubStep(
    incidentId: string,
    stepKey: string,
    subStepKey: string,
    data?: { reopenReasonText?: string }
  ): Promise<HipaaIncidentSubStep> {
    const res = await apiClient.post<HipaaIncidentSubStep>(
      `${BASE}/hipaa/${incidentId}/steps/${stepKey}/sub-steps/${subStepKey}/reopen`,
      data ?? {}
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to reopen sub-step');
    }
    return res.data;
  },

  // ── HIPAA incident documents ─────────────────────────────────────────────

  async listHipaaDocuments(incidentId: string): Promise<HipaaIncidentDoc[]> {
    const res = await apiClient.get<{ items: HipaaIncidentDoc[] }>(`${BASE}/hipaa/${incidentId}/documents`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch documents');
    }
    return res.data.items ?? [];
  },

  async uploadHipaaDocument(
    incidentId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<HipaaIncidentDoc> {
    const res = await apiClient.uploadFile<HipaaIncidentDoc>(
      `${BASE}/hipaa/${incidentId}/documents`,
      file,
      onProgress
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to upload document');
    }
    return res.data;
  },

  async downloadHipaaDocument(incidentId: string, docId: string, filename?: string): Promise<void> {
    await apiClient.downloadFile(`${BASE}/hipaa/${incidentId}/documents/${docId}/download`, filename);
  },

  async deleteHipaaDocument(incidentId: string, docId: string): Promise<void> {
    const res = await apiClient.delete(`${BASE}/hipaa/${incidentId}/documents/${docId}`);
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Failed to delete document');
    }
  },

  async listHipaaDocumentVersions(incidentId: string, docId: string): Promise<HipaaIncidentDoc[]> {
    const res = await apiClient.get<{ versions: HipaaIncidentDoc[] }>(`${BASE}/hipaa/${incidentId}/documents/${docId}/versions`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch versions');
    }
    return res.data.versions ?? [];
  },

  async uploadHipaaDocumentVersion(
    incidentId: string,
    docId: string,
    file: File,
    changeNotes?: string,
    onProgress?: (pct: number) => void
  ): Promise<HipaaIncidentDoc> {
    const res = await apiClient.uploadFile<HipaaIncidentDoc>(
      `${BASE}/hipaa/${incidentId}/documents/${docId}/versions`,
      file,
      onProgress,
      changeNotes ? { changeNotes } : undefined
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to upload version');
    }
    return res.data;
  },

  async downloadHipaaDocumentVersion(incidentId: string, docId: string, versionDocId: string, filename?: string): Promise<void> {
    await apiClient.downloadFile(`${BASE}/hipaa/${incidentId}/documents/${docId}/versions/${versionDocId}/download`, filename);
  },

  async viewHipaaSubStepArtifact(incidentId: string, stepKey: string, subStepKey: string, filename?: string): Promise<void> {
    await apiClient.downloadFile(`${BASE}/hipaa/${incidentId}/documents/by-substep/${stepKey}/${subStepKey}/view`, filename);
  },

  // ── Notification letter templates (VIS-208) — global, not per-incident ──────

  async listNotificationLetterTemplates(): Promise<NotificationLetterTemplate[]> {
    const res = await apiClient.get<{ items: NotificationLetterTemplate[] }>(`${BASE}/hipaa/notification-letter-templates`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to load notification templates');
    }
    return res.data.items ?? [];
  },

  async downloadNotificationLetterTemplate(id: string, filename?: string): Promise<void> {
    await apiClient.downloadFile(`${BASE}/hipaa/notification-letter-templates/${id}/download`, filename);
  },

  async listNotificationLetterTemplateVersions(templateType: NotificationLetterTemplateType): Promise<NotificationLetterTemplate[]> {
    const res = await apiClient.get<{ versions: NotificationLetterTemplate[] }>(`${BASE}/hipaa/notification-letter-templates/${templateType}/versions`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch template versions');
    }
    return res.data.versions ?? [];
  },

  async uploadNotificationLetterTemplateVersion(
    templateType: NotificationLetterTemplateType,
    file: File,
    changeNotes?: string,
    onProgress?: (pct: number) => void
  ): Promise<NotificationLetterTemplate> {
    const res = await apiClient.uploadFile<NotificationLetterTemplate>(
      `${BASE}/hipaa/notification-letter-templates/${templateType}/versions`,
      file,
      onProgress,
      changeNotes ? { changeNotes } : undefined
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to upload template version');
    }
    return res.data;
  },

  async listHipaaActivity(incidentId: string): Promise<HipaaActivity[]> {
    const res = await apiClient.get<{ items: HipaaActivity[] }>(`${BASE}/hipaa/${incidentId}/activity`);
    if (!res.success || !res.data) {throw new Error(res.error?.message ?? 'Failed to load activity');}
    return res.data.items;
  },

  async createHipaaActivityNote(incidentId: string, title: string, details?: string): Promise<HipaaActivity> {
    const res = await apiClient.post<HipaaActivity>(`${BASE}/hipaa/${incidentId}/activity`, { title, details });
    if (!res.success || !res.data) {throw new Error(res.error?.message ?? 'Failed to add note');}
    return res.data;
  },

  async setHipaaLifecycleStepOutcome(
    incidentId: string,
    stepKey: string,
    notificationOutcome: HipaaNotificationOutcome | null
  ): Promise<Record<string, unknown>> {
    const res = await apiClient.patch<Record<string, unknown>>(
      `${BASE}/hipaa/${incidentId}/steps/${stepKey}/outcome`,
      { notificationOutcome }
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to set outcome');
    }
    return res.data;
  },

  async getHipaaDashboard(
    filters?: HipaaDashboardFilters
  ): Promise<HipaaDashboardPayload> {
    const query = new URLSearchParams();
    if (filters?.groupId) {query.set('groupId', filters.groupId);}
    if (filters?.source) {query.set('source', filters.source);}
    if (filters?.severity) {query.set('severity', filters.severity);}
    if (filters?.fundingType) {query.set('fundingType', filters.fundingType);}
    if (filters?.volumeGranularity) {
      query.set('volumeGranularity', filters.volumeGranularity);
    }
    if (filters?.reportingYear !== undefined) {
      query.set('reportingYear', String(filters.reportingYear));
    }
    const qs = query.toString();
    const res = await apiClient.get<HipaaDashboardPayload>(
      `${BASE}/hipaa/dashboard${qs ? `?${qs}` : ''}`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch HIPAA dashboard');
    }
    return res.data;
  },

  async getHipaaDashboardThresholds(): Promise<HipaaDashboardThresholds> {
    const res = await apiClient.get<HipaaDashboardThresholds>(
      `${BASE}/hipaa/dashboard/thresholds`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch thresholds');
    }
    return res.data;
  },

  async updateHipaaDashboardThresholds(
    data: { amberDays?: number; yellowDays?: number }
  ): Promise<HipaaDashboardThresholds> {
    const res = await apiClient.patch<HipaaDashboardThresholds>(
      `${BASE}/hipaa/dashboard/thresholds`,
      data
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to update thresholds');
    }
    return res.data;
  },

  async deleteHipaaIncident(id: string): Promise<void> {
    const res = await apiClient.delete(`${BASE}/hipaa/${id}`);
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Failed to delete HIPAA incident');
    }
  },

  // ── Major Breach Manual-Tracking (VIS-69, spec 3.10.7) ────────────────────

  async listMajorBreachNotifications(
    incidentId: string
  ): Promise<MajorBreachNotification[]> {
    const res = await apiClient.get<{ items: MajorBreachNotification[] }>(
      `${BASE}/hipaa/${incidentId}/major-breach-notifications`
    );
    if (!res.success || !res.data) {
      throw new Error(
        res.error?.message ?? 'Failed to fetch major breach notifications'
      );
    }
    return res.data.items ?? [];
  },

  async saveMajorBreachNotification(
    incidentId: string,
    category: MajorBreachCategory,
    data: MajorBreachNotificationInput
  ): Promise<MajorBreachNotification> {
    const res = await apiClient.put<MajorBreachNotification>(
      `${BASE}/hipaa/${incidentId}/major-breach-notifications/${category}`,
      data
    );
    if (!res.success || !res.data) {
      throw new Error(
        res.error?.message ?? 'Failed to save major breach notification'
      );
    }
    return res.data;
  },

  async uploadMajorBreachAttachment(
    incidentId: string,
    category: MajorBreachCategory,
    file: File
  ): Promise<MajorBreachAttachment> {
    const res = await apiClient.uploadFile<MajorBreachAttachment>(
      `${BASE}/hipaa/${incidentId}/major-breach-notifications/${category}/attachments`,
      file
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to upload attachment');
    }
    return res.data;
  },

  async deleteMajorBreachAttachment(
    incidentId: string,
    category: MajorBreachCategory,
    attachmentId: string
  ): Promise<void> {
    const res = await apiClient.delete(
      `${BASE}/hipaa/${incidentId}/major-breach-notifications/${category}/attachments/${attachmentId}`
    );
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Failed to delete attachment');
    }
  },

  buildMajorBreachAttachmentDownloadUrl(
    incidentId: string,
    category: MajorBreachCategory,
    attachmentId: string
  ): string {
    return `${BASE}/hipaa/${incidentId}/major-breach-notifications/${category}/attachments/${attachmentId}/download`;
  },

  // ── Notification Letters (VIS-206) ────────────────────────────────────────

  async getHipaaNotificationLetterCatalog(
    incidentId: string
  ): Promise<HipaaNotificationLetter[]> {
    const res = await apiClient.get<{ letters: HipaaNotificationLetter[] }>(
      `${BASE}/hipaa/${incidentId}/notification-letters/catalog`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to load notification letters');
    }
    return res.data.letters ?? [];
  },

  async previewHipaaNotificationLetter(
    incidentId: string,
    letterKey: string,
    fields: Record<string, string>
  ): Promise<HipaaNotificationLetterPreview> {
    const res = await apiClient.post<HipaaNotificationLetterPreview>(
      `${BASE}/hipaa/${incidentId}/notification-letters/preview`,
      { letterKey, fields }
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to preview notification letter');
    }
    return res.data;
  },

  async sendHipaaNotificationLetter(
    incidentId: string,
    payload: { letterKey: string; fields: Record<string, string>; recipientName?: string }
  ): Promise<HipaaSendNotificationLetterResult> {
    const res = await apiClient.post<HipaaSendNotificationLetterResult>(
      `${BASE}/hipaa/${incidentId}/notification-letters/send`,
      payload
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to send notification letter');
    }
    return res.data;
  },

  async listHipaaSentNotifications(
    incidentId: string
  ): Promise<HipaaSentNotification[]> {
    const res = await apiClient.get<{ items: HipaaSentNotification[] }>(
      `${BASE}/hipaa/${incidentId}/notification-letters`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to load sent notifications');
    }
    return res.data.items ?? [];
  },

  // ── Custom Contracts ──────────────────────────────────────────────────────

  async getCustomContracts(params?: { page?: number; limit?: number; status?: string; handler?: string; priority?: string; accountName?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) {query.set('page', String(params.page));}
    if (params?.limit) {query.set('limit', String(params.limit));}
    if (params?.status) {query.set('status', params.status);}
    if (params?.handler) {query.set('handler', params.handler);}
    if (params?.priority) {query.set('priority', params.priority);}
    if (params?.accountName) {query.set('accountName', params.accountName);}
    if (params?.search) {query.set('search', params.search);}
    const res = await apiClient.get<Record<string, unknown>>(`${BASE}/custom-contracts?${query.toString()}`);
    if (!res.success || !res.data) {throw new Error(res.error?.message ?? 'Failed to fetch custom contracts');}
    return res.data;
  },

  async getCustomContract(id: string) {
    const res = await apiClient.get<Record<string, unknown>>(`${BASE}/custom-contracts/${id}`);
    if (!res.success || !res.data) {throw new Error(res.error?.message ?? 'Failed to fetch custom contract');}
    return (res.data as { request?: unknown }).request ?? res.data;
  },

  async createCustomContract(data: unknown) {
    const res = await apiClient.post<Record<string, unknown>>(`${BASE}/custom-contracts`, data);
    if (!res.success || !res.data) {throw new Error(res.error?.message ?? 'Failed to create custom contract');}
    return res.data;
  },

  async updateCustomContract(id: string, data: unknown) {
    const res = await apiClient.put<Record<string, unknown>>(`${BASE}/custom-contracts/${id}`, data);
    if (!res.success || !res.data) {throw new Error(res.error?.message ?? 'Failed to update custom contract');}
    return res.data;
  },

  async updateCustomContractStatus(id: string, data: { status: string; reason?: string }) {
    const res = await apiClient.patch<Record<string, unknown>>(`${BASE}/custom-contracts/${id}/status`, data);
    if (!res.success || !res.data) {throw new Error(res.error?.message ?? 'Failed to update status');}
    return res.data;
  },

  async getCustomContractDashboard() {
    const res = await apiClient.get<Record<string, unknown>>(`${BASE}/custom-contracts/dashboard`);
    if (!res.success || !res.data) {throw new Error(res.error?.message ?? 'Failed to fetch dashboard');}
    return res.data;
  },

  async deleteCustomContract(id: string) {
    const res = await apiClient.delete(`${BASE}/custom-contracts/${id}`);
    if (!res.success) {throw new Error(res.error?.message ?? 'Failed to delete');}
  },

  // ── HIPAA Business Entities (VIS-129) ─────────────────────────────────────

  async getBusinessEntities(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: BusinessEntityStatus;
    includeDeleted?: boolean;
  }): Promise<{
    items: BusinessEntity[];
    pagination?: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const query = new URLSearchParams();
    if (params?.page) {query.set('page', String(params.page));}
    if (params?.limit) {query.set('limit', String(params.limit));}
    if (params?.search) {query.set('search', params.search);}
    if (params?.status) {query.set('status', params.status);}
    if (params?.includeDeleted) {query.set('includeDeleted', 'true');}

    const qs = query.toString();
    const res = await apiClient.get<{ items: BusinessEntity[] }>(
      `${BASE}/hipaa/business-entities${qs ? `?${qs}` : ''}`
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch business entities');
    }
    const meta = res.meta as
      | { pagination?: { total: number; page: number; limit: number; totalPages: number } }
      | undefined;
    return { items: res.data.items ?? [], pagination: meta?.pagination };
  },

  async getBusinessEntity(id: string): Promise<BusinessEntity> {
    const res = await apiClient.get<BusinessEntity>(`${BASE}/hipaa/business-entities/${id}`);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to fetch business entity');
    }
    return res.data;
  },

  async createBusinessEntity(data: BusinessEntityInput): Promise<BusinessEntity> {
    const res = await apiClient.post<BusinessEntity>(`${BASE}/hipaa/business-entities`, data);
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to create business entity');
    }
    return res.data;
  },

  async updateBusinessEntity(
    id: string,
    data: Partial<BusinessEntityInput>
  ): Promise<BusinessEntity> {
    const res = await apiClient.patch<BusinessEntity>(
      `${BASE}/hipaa/business-entities/${id}`,
      data
    );
    if (!res.success || !res.data) {
      throw new Error(res.error?.message ?? 'Failed to update business entity');
    }
    return res.data;
  },

  async deleteBusinessEntity(id: string): Promise<void> {
    const res = await apiClient.delete(`${BASE}/hipaa/business-entities/${id}`);
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Failed to delete business entity');
    }
  },
};
